import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from '../utils/toast';
import { geminiApi } from '../services/geminiApi';
import DeploymentOptions from '../components/DeploymentOptions';
import * as pdfjsLib from 'pdfjs-dist';

export function AutoRag() {
  const [files, setFiles] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isThinking, setIsThinking] = useState(false);

  useEffect(() => {
    // Set up PDF.js worker
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
  }, []);

  const extractTextFromPdf = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';
      
      // Extract text from each page
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }
      
      return fullText;
    } catch (error) {
      console.error('Error extracting text from PDF:', error);
      throw new Error('Failed to extract text from PDF');
    }
  };

  const processTextInChunks = async (text) => {
    const chunkSize = 12000; // Smaller than 15k to leave room for prompt
    const chunks = [];
    
    // Split text into chunks
    for (let i = 0; i < text.length; i += chunkSize) {
      chunks.push(text.slice(i, i + chunkSize));
    }

    const summaries = [];
    
    // Process each chunk
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const prompt = `Please analyze and summarize the following text content (Part ${i + 1} of ${chunks.length}). Focus on:
1. Main topics and themes
2. Key points or arguments
3. Important findings or conclusions
4. Document structure and organization

Text content:
${chunk}

Please provide a concise but comprehensive summary.`;

      try {
        const response = await geminiApi.generateText(prompt);
        if (response.error) throw new Error(response.error);
        summaries.push(response.content || response.message);
      } catch (error) {
        console.error(`Error processing chunk ${i + 1}:`, error);
        summaries.push(`Error processing part ${i + 1} of the document`);
      }
    }

    // If we have multiple summaries, combine them
    if (summaries.length > 1) {
      const combiningPrompt = `Please combine the following document section summaries into a coherent overall summary:

${summaries.map((summary, index) => `Part ${index + 1}:\n${summary}`).join('\n\n')}

Please provide a comprehensive summary that captures the key points from all sections.`;

      try {
        const response = await geminiApi.generateText(combiningPrompt);
        return response.content || response.message;
      } catch (error) {
        console.error('Error combining summaries:', error);
        return summaries.join('\n\n');
      }
    }

    return summaries[0];
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setIsProcessing(true);
    const newFiles = [...files];
    const newSummaries = { ...summaries };

    try {
      for (const file of acceptedFiles) {
        // Check for supported file types
        if (!file.type.startsWith('text/') && 
            !file.name.endsWith('.txt') && 
            !file.name.endsWith('.md') && 
            file.type !== 'application/pdf' &&
            !file.name.endsWith('.pdf')) {
          toast.error(`${file.name} is not a supported file type`);
          continue;
        }

        let text;
        if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
          // Handle PDF files
          text = await extractTextFromPdf(file);
        } else {
          // Handle text files
          text = await readFileContent(file);
        }

        newFiles.push(file);

        try {
          // Process the entire document in chunks
          const summary = await processTextInChunks(text);
          
          newSummaries[file.name] = {
            summary,
            timestamp: new Date().toISOString()
          };
        } catch (error) {
          console.error('Error generating summary:', error);
          toast.error(`Failed to generate summary for ${file.name}`);
          newSummaries[file.name] = {
            summary: 'Failed to generate summary',
            error: true,
            timestamp: new Date().toISOString()
          };
        }
      }

      setFiles(newFiles);
      setSummaries(newSummaries);
      toast.success('Files processed successfully!');
    } catch (error) {
      console.error('Error processing files:', error);
      toast.error('Failed to process some files');
    } finally {
      setIsProcessing(false);
    }
  }, [files, summaries]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/*': ['.txt', '.md'],
      'application/pdf': ['.pdf']
    }
  });

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  };

  const removeFile = (fileName) => {
    setFiles(files.filter(file => file.name !== fileName));
    const newSummaries = { ...summaries };
    delete newSummaries[fileName];
    setSummaries(newSummaries);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!currentMessage.trim() || files.length === 0) return;

    const newMessage = {
      content: currentMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, newMessage]);
    setCurrentMessage('');
    setIsThinking(true);

    try {
      // Construct context from all file summaries
      const context = Object.entries(summaries)
        .map(([filename, data]) => `Document: ${filename}\nSummary: ${data.summary}\n`)
        .join('\n');

      const prompt = `Based on the following document summaries:

${context}

Please answer this question: ${currentMessage}

Provide a clear and concise answer based on the information available in the documents.`;

      const response = await geminiApi.generateText(prompt);

      const aiMessage = {
        content: response.content || response.message,
        sender: 'ai',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error generating response:', error);
      toast.error('Failed to generate response');
    } finally {
      setIsThinking(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern SaaS Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-slate-400/20 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-slate-800/50 border border-slate-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-slate-400 animate-pulse"></div>
                <span className="text-sm text-slate-100">AI-Powered Analysis</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                AutoRag
              </h1>
              <p className="text-lg text-slate-200 max-w-xl">
                Drop your files to automatically analyze and summarize content using RAG technology
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left column - File upload and summaries */}
        <div className="space-y-8">
          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors duration-200 ${
              isDragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-primary-500 hover:bg-gray-50'
            }`}
          >
            <input {...getInputProps()} />
            <div className="space-y-4">
              <div className="flex justify-center">
                <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <div>
                <p className="text-lg font-medium text-gray-900">
                  {isDragActive ? 'Drop your files here' : 'Drag & drop files here'}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  or click to select files
                </p>
              </div>
              <div className="text-xs text-gray-500">
                Supported file types: .txt, .md, .pdf
              </div>
            </div>
          </div>

          {/* Processing indicator */}
          {isProcessing && (
            <div className="flex items-center justify-center p-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
              <span className="ml-3 text-gray-600">Processing files...</span>
            </div>
          )}

          {/* File list and summaries */}
          {files.length > 0 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Processed Files</h2>
              <div className="space-y-4">
                {files.map((file) => (
                  <div key={file.name} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">{file.name}</h3>
                            <p className="text-sm text-gray-500">
                              {new Date(summaries[file.name]?.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFile(file.name)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      {summaries[file.name] && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-900">Summary</h4>
                          <div className={`mt-2 text-sm ${
                            summaries[file.name].error ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {summaries[file.name].summary}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column - Chat interface */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col h-[600px]">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Chat with Your Documents</h2>
            <p className="text-sm text-gray-500">Ask questions about your uploaded documents</p>
          </div>

          {/* Messages container */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.sender === 'user'
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <p className="text-sm">{message.content}</p>
                  <span className="text-xs opacity-75 mt-1 block">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
            {isThinking && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg p-3 flex items-center space-x-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            )}
          </div>

          {/* Message input */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
            <div className="flex space-x-4">
              <input
                type="text"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                placeholder="Ask a question about your documents..."
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={files.length === 0}
              />
              <button
                type="submit"
                disabled={!currentMessage.trim() || files.length === 0 || isThinking}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send
              </button>
            </div>
            {files.length === 0 && (
              <p className="mt-2 text-sm text-gray-500">
                Upload some documents to start chatting
              </p>
            )}
          </form>
        </div>
      </div>
      
      <DeploymentOptions appId="auto-rag" position="bottom" />
    </div>
  );
}

export default AutoRag; 